import { pxToRem, Spinner, Text, useUpdateEffect } from '@lbcamden/lib-react';
import { skipToken } from '@tanstack/react-query';
import { EffectCallback, FunctionComponent, useEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { useHandlePinAuth } from '~/client/hooks/authHooks';
import { logout } from '~/client/lib/auth';
import { api } from '~/client/lib/trpc';
import { maxUserPinFailureCount } from '~/common/config/constants';

import { PinInput } from '../../common/PinInput';
import { getPinFailureErrorMessage } from './helpers';
import { UserPinAuthRoot } from './UserPinAuth.styled';

type Props = {
  isClientAuth?: boolean;
  onUnexpectedError?(): void;
};

export const pinResetDelayMs = 2000;

export const UserPinAuth: FunctionComponent<Props> = ({ isClientAuth, onUnexpectedError }) => {
  const [clientAuthAborted, storeClientAuthAborted] = useLocalStorage<boolean>(
    'clientAuthAborted',
    false,
  );

  const [pin, setPin] = useState('');
  const [pinFailureCount, setPinFailureCount] = useState(0);
  const [success, setSuccess] = useState(false);

  const { handleAuth, handleClientAuth, resetUserPin, authenticating, username } = useHandlePinAuth(
    {
      onAuthFailure: (isUnauthorised) => {
        if (isUnauthorised) {
          setPinFailureCount((current) => current + 1);
          setPin('');
        } else {
          onUnexpectedError?.();
        }
      },
    },
  );

  const { data: serverSideFailureCount = 0, refetch: refetchFailureCount } =
    api.getUserPinFailedAttemptCount.useQuery(username ? { username } : skipToken, {
      enabled: !isClientAuth,
    });

  const errorMessage = getPinFailureErrorMessage(pinFailureCount);
  const willResetPin = pinFailureCount >= maxUserPinFailureCount;
  const loading = authenticating || success;

  const detectClientAuthAborted: EffectCallback = () => {
    if (isClientAuth) {
      const listener = () => storeClientAuthAborted(true);
      window.addEventListener('beforeunload', listener);
      return () => {
        window.removeEventListener('beforeunload', listener);
      };
    }
  };

  // Logout if client auth was previously aborted, to prevent the circumvention of attempt limits
  const preventClientAuthRefresh: EffectCallback = () => {
    if (clientAuthAborted) {
      void logout();
    }
    storeClientAuthAborted(false);
  };

  const handlePinReset = () =>
    setTimeout(async () => {
      await resetUserPin();
      setPinFailureCount(0);
    }, pinResetDelayMs); // Give a small delay to display the final error message

  const onComplete = async (pinValue: string) => {
    const auth = isClientAuth ? handleClientAuth : handleAuth;
    setSuccess(!!(await auth(pinValue)));
  };

  useEffect(detectClientAuthAborted, [isClientAuth]);

  useEffect(preventClientAuthRefresh, []);

  useEffect(() => {
    if (!isClientAuth) {
      setPinFailureCount(serverSideFailureCount);
    }
  }, [serverSideFailureCount]);

  useUpdateEffect(() => {
    if (!isClientAuth) {
      void refetchFailureCount();
    }
  }, [pinFailureCount]);

  useEffect(() => {
    if (willResetPin) {
      handlePinReset();
    }
  }, [willResetPin]);

  return (
    <UserPinAuthRoot>
      {loading && <Spinner $size={pxToRem(38)} />}

      {!loading && (
        <>
          {!willResetPin && <PinInput value={pin} onChange={setPin} onComplete={onComplete} />}
          {errorMessage && (
            <Text css={{ whiteSpace: 'pre-wrap' }} variant="body2" $color="error">
              {errorMessage}
            </Text>
          )}
        </>
      )}
    </UserPinAuthRoot>
  );
};
